import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

type CardType = 'changeControl' | 'qualityEvent';

interface AddCardModalProps {
  cardType: CardType;
  onAdd: (card: any) => void;
  onClose: () => void;
}

const CC_STATUS_OPTIONS = ['Open', 'In Progress', 'Closed', 'Initiation', 'Initial Review'];
const QE_TYPE_OPTIONS = ['Deviation', 'OutOfSpecification', 'Complaint', 'MarketComplaint', 'OutOfTrend'];
const QE_STATUS_OPTIONS = ['Open', 'Closed', 'Not available'] as const;
const QE_STATUS_TO_APPROVAL: Record<string, boolean | null> = {
  'Open': false,
  'Closed': true,
  'Not available': null,
};

function generateStableId(cardType: CardType): string {
  const prefix = cardType === 'changeControl' ? 'manual_cc' : 'manual_qe';
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export const AddCardModal: React.FC<AddCardModalProps> = ({ cardType, onAdd, onClose }) => {
  const [name, setName] = useState('');
  const [titleValue, setTitleValue] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(CC_STATUS_OPTIONS[0]);
  const [eventType, setEventType] = useState(QE_TYPE_OPTIONS[0]);
  const [qeStatus, setQeStatus] = useState<string>(QE_STATUS_OPTIONS[0]);
  const [finalApprovalDate, setFinalApprovalDate] = useState('');
  const [targetCompletionDate, setTargetCompletionDate] = useState('');

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const stableId = generateStableId(cardType);

    if (cardType === 'changeControl') {
      onAdd({
        stableId,
        actionId: name.trim(),
        text: description,
        status,
        source: 'Manual Entry',
        actions: [],
      });
    } else {
      onAdd({
        stableId,
        id: name.trim(),
        eventType,
        titleValue: titleValue.trim() || '',
        description,
        affectedProducts: '',
        affectedBatchNumber: '',
        finalApprovalDate,
        finalApprovalCompleted: QE_STATUS_TO_APPROVAL[qeStatus] ?? null,
        targetCompletionDate,
        matchedVia: 'Manual Entry',
      });
    }

    onClose();
  };

  const isCC = cardType === 'changeControl';
  const title = isCC ? 'Add Change Control' : 'Add Quality Event';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="add-card-name" className="block text-xs font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="add-card-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isCC ? 'e.g. CC-0001234' : 'e.g. DEV-0005678'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {isCC ? (
            <div>
              <label htmlFor="add-card-status" className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                id="add-card-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {CC_STATUS_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="add-card-event-type" className="block text-xs font-medium text-gray-700 mb-1">Event Type</label>
                <select
                  id="add-card-event-type"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {QE_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="add-card-title" className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input
                  id="add-card-title"
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  placeholder="Short title for this quality event"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="add-card-qe-status" className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  id="add-card-qe-status"
                  value={qeStatus}
                  onChange={(e) => setQeStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {QE_STATUS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="add-card-final-approval" className="block text-xs font-medium text-gray-700 mb-1">Final Approval Date</label>
                <input
                  id="add-card-final-approval"
                  type="date"
                  value={finalApprovalDate}
                  onChange={(e) => setFinalApprovalDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="add-card-target-completion" className="block text-xs font-medium text-gray-700 mb-1">Target Completion Date</label>
                <input
                  id="add-card-target-completion"
                  type="date"
                  value={targetCompletionDate}
                  onChange={(e) => setTargetCompletionDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="add-card-description" className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="add-card-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
